import {FC, useEffect, useState} from 'react'
import {StyledRestrictionContent, StyledRestrictionLayerWrapper, StyledVariant} from './styled'
import {Button} from '@/components/ui/button/Button'
import {EyeIcon, EyeOffIcon} from '@/components/ui/icon'
import {VerifyAgeModal} from './components/verify-age-modal/VerifyAgeModal'
import {useMe} from '@/features/authentication/queries/useMe'
import {UserRoleE} from '@/types'
import {GuestTypeV3} from '@/features/authentication/types'
import {useTranslation} from 'react-i18next'
import {MURAL_TYPES} from '@/features/profile/services/completeProfile.schema'
import {UPDATE_VARIANT, useGetContentById} from '@/features/feed/queries/useGetContentById'

type RestrictionLayerProps = {
    isRestricted?: boolean
    mediaId: string
    mediaType?: MURAL_TYPES
    creatorId?: string
    variant: UPDATE_VARIANT
    isBlurred?: boolean
    chatIdentifier?: string
} & React.HTMLAttributes<HTMLDivElement>

export const RestrictionLayer: FC<RestrictionLayerProps> = ({
    isRestricted = false,
    mediaId,
    mediaType = MURAL_TYPES.PHOTOS,
    variant,
    isBlurred = false,
    creatorId = '',
    children,
    chatIdentifier = ''
}) => {
    const {t} = useTranslation()
    const {data: user} = useMe(isRestricted)
    const isGuest = user && user.type === UserRoleE.Guest
    const isHost = user && user.type === UserRoleE.Host
    const {mutate: getContent} = useGetContentById({mediaType, variant, creatorId, chatIdentifier})
    const isCountryRestricted = user && isGuest && (user as GuestTypeV3).typeAttributes.isCountryRestricted
    const isAgeVerifiedByGoCam = user && (user as GuestTypeV3).isAgeVerified

    const [isOpenVerifyAgeModal, setIsOpenVerifyAgeModal] = useState(
        mediaType === MURAL_TYPES.MEDIA_PACKAGES ? false : !isBlurred
    )
    const isCanGetMedia = !isCountryRestricted || isAgeVerifiedByGoCam

    useEffect(() => {
        isRestricted && mediaType !== MURAL_TYPES.MEDIA_PACKAGES && setIsOpenVerifyAgeModal(!isBlurred)
    }, [isBlurred])

    if (isHost) return children
    if (isOpenVerifyAgeModal && isCanGetMedia) return children

    return isRestricted ? (
        <>
            <StyledRestrictionLayerWrapper styledVariant={StyledVariant[variant]}>
                <StyledRestrictionContent>
                    <EyeOffIcon width={48} height={48} />
                    <h1>{t('restricted_content:title')}</h1>
                    <p>{t('restricted_content:description')}</p>
                    <Button
                        onClick={e => {
                            e.stopPropagation()
                            if (isCanGetMedia) getContent(mediaId)

                            setIsOpenVerifyAgeModal(true)
                        }}
                    >
                        <EyeIcon width={24} height={24} /> {t('restricted_content:view')}
                    </Button>
                </StyledRestrictionContent>
                {children}
            </StyledRestrictionLayerWrapper>
            {isOpenVerifyAgeModal && <VerifyAgeModal onClose={() => setIsOpenVerifyAgeModal(false)} />}
        </>
    ) : (
        children
    )
}
